import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Drawer, Form, Input, Switch, Button, Typography } from "antd";
import JoditEditor from "jodit-react";
import { request } from "../../../functions/request";
import { toast } from "react-toastify";
import { limparColagemDeLinhas, removerParagrafos } from "../../../functions/utils";

function ModalAddAtalho({ isOpen, onClose, editData, onSaved }) {
  const [atalho, setAtalho] = useState("");
  const [ativo, setAtivo] = useState(true);
  const [content, setContent] = useState("");
  const editorRef = useRef(null);

  // Mesmo gancho de colagem dos editores de notícia/análise. O conteúdo dos
  // atalhos costuma vir colado do Word (daí os <p class="MsoNormal"> que
  // apareceram nos atalhos antigos): limpar na hora da colagem deixa o
  // usuário ver o resultado final na tela, em vez de o texto mudar sozinho
  // só depois de salvar.
  const setEditorInstance = useCallback((instance) => {
    editorRef.current = instance;
    if (instance?.e) {
      instance.e.off("beforePasteInsert", limparColagemDeLinhas);
      instance.e.on("beforePasteInsert", limparColagemDeLinhas);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setAtalho(editData?.atl_atalho || "");
      setAtivo(editData?.atl_ativo ?? true);
      setContent(editData?.atl_conteudo || "");
    }
  }, [isOpen, editData]);

  // Config precisa de identidade estável entre renders, senão o jodit-react
  // recria a instância inteira do editor a cada re-render (mesmo fix
  // aplicado em EditorNoticia.js/EditorAnalise.js/ModalAddModelo.js).
  const joditConfig = useMemo(
    () => ({
      language: "pt_br",
      width: "100%",
      minHeight: "250px",
      spellcheck: true,
      spellcheckLang: "pt-BR",
      // enter:"br" foi tentado aqui pra desabilitar o uso de <p> ao dar
      // Enter, mas causa perda de conteúdo: ao desfazer uma lista (clicar
      // de novo no botão de lista), o texto do último item simplesmente
      // desaparece em vez de virar parágrafo — bug confirmado do Jodit
      // quando enter="br" (não acontece com o padrão "p"). Revertido.
    }),
    []
  );

  const saveData = async () => {
    const atalhoLimpo = atalho.trim().replace(/^\/+/, "").replace(/\s+/g, "");
    if (!atalhoLimpo || !content.trim()) {
      toast.error("Preencha o atalho e o conteúdo!");
      return;
    }

    try {
      const payload = {
        newData: {
          atl_atalho: atalhoLimpo,
          atl_conteudo: removerParagrafos(content),
          atl_ativo: ativo ? 1 : 0,
        },
      };

      if (editData?.atl_codigo) {
        await request(`/atalho/updateAtalho/${editData.atl_codigo}`, {
          method: "PUT",
          body: JSON.stringify(payload),
        });
      } else {
        await request("/atalho/insertAtalho", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }

      toast.success("Atalho salvo com sucesso!");
      onClose();
      onSaved();
    } catch (err) {
      console.log(err);
      toast.error(err?.message?.includes("409") ? "Já existe um atalho com esse nome!" : "Erro ao salvar atalho!");
    }
  };

  return (
    <Drawer
      title={editData?.atl_codigo ? "Editar Atalho" : "Adicionar Novo Atalho"}
      open={isOpen}
      onClose={onClose}
      width={640}
      extra={
        <Button type="primary" onClick={saveData}>
          Salvar
        </Button>
      }
    >
      <Form layout="vertical">
        <Form.Item label="Atalho (digite no editor: /atalho + barra de espaço)">
          <Input
            addonBefore="/"
            placeholder="ex: fechamento"
            value={atalho}
            onChange={(e) => setAtalho(e.target.value)}
          />
        </Form.Item>
        <Form.Item label="Ativo">
          <Switch checked={ativo} onChange={(v) => setAtivo(v)} />
        </Form.Item>
        <Form.Item label="Conteúdo (o que será inserido)">
          {isOpen && (
            <JoditEditor
              editorRef={setEditorInstance}
              value={content}
              config={joditConfig}
              onBlur={(newContent) => setContent(newContent)}
            />
          )}
        </Form.Item>
        <Typography.Paragraph type="secondary">
          No editor de notícias/análises, digite <b>/{atalho.trim().replace(/^\/+/, "") || "atalho"}</b> seguido de um espaço pra expandir automaticamente.
        </Typography.Paragraph>
      </Form>
    </Drawer>
  );
}

export default ModalAddAtalho;
